// Board of directors — M2 (ARCHITECTURE_M2.md §4). Design §3/§11 in light form:
// one confidence meter (0–100), moved by what actually happened this tick and
// reviewed at the quarterly meeting ritual. Consequences escalate: financing-term
// haircuts (< concernFloor, applied in term-sheet generation), a board-imposed
// hiring freeze (< ultimatumFloor), and founder ouster (≤ ousterFloor once
// investors hold enough seats). Perks above confHighFloor arrive the same way —
// better term sheets. Angels don't hold formal meetings; the ritual starts at VC.

import type { TickContext } from "../ctx.js";
import { emit } from "../ctx.js";
import { clamp } from "../prng.js";

/** Map this tick's engine events onto confidence deltas (content-tuned). */
function eventDeltas(ctx: TickContext): number {
  const d = ctx.content.economy.board.deltas;
  let delta = 0;
  for (const e of ctx.events) {
    switch (e.type) {
      case "roundClosed":
        delta += d.roundClosed ?? 0;
        if (e.data.downRound === true) delta += d.downRound ?? 0;
        break;
      case "roundFailed":
        delta += d.roundFailed ?? 0;
        break;
      case "siliconWorks":
        delta += d.siliconWorks ?? 0;
        break;
      case "siliconErrata":
        delta += d.siliconErrata ?? 0;
        break;
      case "siliconDead":
        delta += d.siliconDead ?? 0;
        break;
      case "productReleased":
        delta += d.productReleased ?? 0;
        break;
      case "fieldFailure":
        delta += d.fieldFailure ?? 0;
        break;
      case "ipoPulled":
        delta += d.ipoPulled ?? 0;
        break;
      case "ipoPriced":
        delta += d.ipoPriced ?? 0;
        if (typeof e.data.firstDayReturn === "number" && e.data.firstDayReturn < -0.1) {
          delta += d.brokenIpo ?? 0; // priced at the top, broke on day one
        }
        break;
      case "earnings":
        if (e.data.result === "beat") delta += d.earningsBeat ?? 0;
        if (e.data.result === "miss") delta += d.earningsMiss ?? 0;
        break;
      case "delistWarning":
        delta += d.delistWarning ?? 0;
        break;
      default:
        break;
    }
  }
  return delta;
}

export function processBoard(ctx: TickContext): void {
  const { s } = ctx;
  const cfg = ctx.content.economy.board;
  const board = s.board;
  if (s.dead) return;

  // continuous drift toward baseline + event-driven moves land immediately
  board.confidence += cfg.meanReversionMonthly * (cfg.baseline - board.confidence) * ctx.monthsPerTick;
  board.confidence = clamp(board.confidence + eventDeltas(ctx), 0, 100);

  // the quarterly meeting ritual (from the VC stage on — angels don't hold court)
  if (s.stage === "angel") return;
  const isQuarterEnd = s.monthIndex % 3 === 2;
  if (!isQuarterEnd) return;
  board.lastMeetingMonth = s.monthIndex;

  // the vote happens on the confidence the directors WALK IN with; the
  // structural runway review lands after and shapes next quarter's posture
  const conf = Math.round(board.confidence);
  if (board.confidence <= cfg.ousterFloor && board.investorSeats >= cfg.ousterMinInvestorSeats) {
    s.dead = true;
    s.deathMonth = s.monthIndex;
    s.deathReason = "ousted";
    emit(ctx, "founderOusted", "The board has voted. You are out. The company continues without you.", {
      confidence: conf,
      investorSeats: board.investorSeats,
    });
    return;
  }
  if (board.confidence < cfg.ultimatumFloor) {
    if (!board.hiringFreeze) {
      board.hiringFreeze = true;
      emit(
        ctx,
        "boardUltimatum",
        "Board ultimatum: hiring is frozen until management shows a credible path. Deliver, or the next vote is about you.",
        { confidence: conf },
      );
    }
  } else if (board.hiringFreeze && board.confidence >= cfg.freezeLiftFloor) {
    board.hiringFreeze = false;
    emit(ctx, "boardMeeting", "The board lifts the hiring freeze — confidence is recovering.", {
      confidence: conf,
      freezeLifted: true,
    });
  } else if (board.confidence < cfg.concernFloor) {
    emit(
      ctx,
      "boardMeeting",
      `Quarterly board meeting: confidence is low (${conf}/100). Expect worse terms on the next round.`,
      { confidence: conf, concerned: true },
    );
  } else {
    emit(ctx, "boardMeeting", `Quarterly board meeting held (confidence ${conf}/100).`, {
      confidence: conf,
      concerned: false,
    });
  }

  // structural review: runway health vs the stage floor
  const kpi = s.kpiHistory[s.kpiHistory.length - 1];
  const runway = kpi ? kpi.runwayMonths : 999;
  if (runway < 6) board.confidence -= cfg.runwayFloorPenalty;
  else if (runway > cfg.runwayHealthyMonths) board.confidence += cfg.runwayHealthyBonus;
  board.confidence = clamp(board.confidence, 0, 100);
}
